import React, { useState, useEffect } from 'react';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';
import { useToast } from '@chakra-ui/react';

import { deleteCases, fetchCases } from 'api/items';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { CasesProps } from './Cases.props';

import CasesView from './Cases.view';

const CasesContainer = ({ compType, embedded }: CasesProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [sku, setSku] = useState<string[]>();
  const [page, setPage] = useState<number>(1);
  const [venue, setVenue] = useState<string[]>();
  const [status, setStatus] = useState<undefined | number[]>(undefined);
  const [urlRecord, setUrlRecord] = useState({});
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();

  const queryUrl = useQueryParams();

  const washhubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washhubId);

  const queryKey = [`fetch-cases-${washhubId}`, page, status, sku, venue];

  const queryFn = () =>
    fetchCases({
      sku,
      venue,
      page,
      limit: 10,
      washHub: washhubId,
      status,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  const { data, isFetching } = useQuery(queryKey, queryFn, options);

  useEffect(() => {
    const urlParams = {
      washhubId: String(washhubId),
      ...(compType ? { compType } : {}),
      ...(type ? { type } : {}),
      ...(venue && venue.length ? { venue: venue.join('-') } : {}),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
    };
    setUrlRecord(urlParams);
  }, [washhubId, compType, status, venue, sku, type]);

  useEffect(() => {
    const { status, venue, sku, type } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    venue && setVenue(venue.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  const invalidateQueries = () => {
    queryClient.invalidateQueries(`fetch-cases-${washhubId}`);

    queryClient.invalidateQueries(`fetch-cases-status-count-${washhubId}`, {
      exact: true,
    });
  };

  const { mutate, isLoading: isDeleting } = useMutation(deleteCases, {
    onSuccess: () => {
      invalidateQueries();
    },
  });

  useWashHubListener(() => {
    setPage(1);
    setSku(undefined);
    setVenue(undefined);
    setStatus(undefined);
  });

  const cases = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          duration: 3000,
          title: 'Success',
          isClosable: true,
          status: 'success',
          description: 'Cases Deleted',
        });

        queryClient.invalidateQueries(queryKey, { exact: true });

        _onSuccess();
      },
    });

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);
    if (type === 'sku') setSku(data);
    if (type === 'site') setVenue(data);
  };

  const onClearFilters = () => {
    setPage(1);
    setSku(undefined);
    setVenue(undefined);
  };

  const onUpdateClick = () => {
    setPage(1);
    setSku(undefined);
    setVenue(undefined);
    setStatus(undefined);

    invalidateQueries();
  };

  const _props = {
    embedded,
    venue: type ? (type == 'sku' ? sku : venue) : [],
    sku,
    cases,
    status,
    onDelete,
    pagination,
    isDeleting,
    onUpdateClick,
    onClearFilters,
    onApplyFilters,
    isLoading: isFetching,
    setPage,
    setStatus,
  };

  if (!isUrlMapped) return null;

  return <CasesView {..._props} />;
};

export default CasesContainer;
